import { LANGUAGES } from '@/constants/countries';
import { getTenantConfigs } from '@/constants/tenant-configs';

import { Tenant } from '@/types/common';
import { Option } from '@/types/components/common';
import { Language } from '@/types/i18n';
import { Territory } from '@/types/places';

export function getLanguageOptions(tenant: Tenant) {
  const availableLanguages: Language[] =
    getTenantConfigs(tenant)?.availableLanguages ?? [];
  if (availableLanguages.length > 1) {
    return availableLanguages.reduce((acc, lang) => {
      const langOption = LANGUAGES.find(l => l.value === lang);
      if (langOption) {
        acc.push(langOption);
      }
      return acc;
    }, [] as Option[]);
  }
  return [];
}

export function negotiateUserLanguage({
  userLang,
  i18nLanguage,
  allowedLanguages,
}: {
  userLang?: string;
  i18nLanguage: string;
  allowedLanguages: Language[];
}): Language {
  if (userLang && allowedLanguages.includes(userLang as Language)) {
    return userLang as Language;
  }
  if (i18nLanguage && allowedLanguages.includes(i18nLanguage as Language)) {
    return i18nLanguage as Language;
  }
  return Language.English;
}

export function getLanguageFromAvailable(
  availableLanguages: Language[],
  preferredLanguage?: string | null,
): Language {
  const effectiveAvailableLanguages =
    availableLanguages.length > 0 ? availableLanguages : [Language.English];
  if (
    preferredLanguage &&
    effectiveAvailableLanguages.includes(preferredLanguage as Language)
  ) {
    return preferredLanguage as Language;
  }
  return effectiveAvailableLanguages[0];
}

export type TerritoryByCountryCodeMap = Record<
  Territory['countryCode'],
  Territory
>;

export function getTenantForCountryCode(
  territoryByCountryCode: TerritoryByCountryCodeMap,
  countryCode: string,
): Tenant {
  const tenantId = territoryByCountryCode[countryCode]?.tenant?.toLowerCase();
  return tenantId as Tenant;
}

export function saveCountryCodeAndSwitchTenant(
  countryCode: string,
  territoryByCountryCode: TerritoryByCountryCodeMap,
  setSavedCountry: (countryCode: string) => void,
  switchTenant: (tenant: Tenant) => void | Promise<void>,
): Tenant | undefined {
  setSavedCountry(countryCode);
  const tenant = getTenantForCountryCode(territoryByCountryCode, countryCode);
  if (!tenant) {
    return undefined;
  }
  void switchTenant(tenant);
  return tenant;
}
